import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { firstError, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { Carousel } from "@nexus/widget-kit";
import { EVENT_CDN_THIEVES_CONTRACT } from "./types";
import type { EventCdnThief, EventCdnThievesData } from "./types";

export interface EventCdnThievesConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "event-cdn-thieves",
  name: "CDN thieves",
  description: "One protected event's CDN-theft infrastructure providers.",
  version: "1.0.0",
  dataRequests: {
    thieves: {
      source: "protection-overview-event-cdn-thieves",
      contract: EVENT_CDN_THIEVES_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: EventCdnThievesConfig = {};

const numberFormat = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const percentFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

function formatDurationHM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h${m.toString().padStart(2, "0")}`;
}

function ThiefCard({ thief, rank }: { thief: EventCdnThief; rank: number }) {
  const unavailPct = Math.min(1, Math.max(0, thief.cdnUnavailPct));

  return (
    <div className="flex w-72 shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-primary">{thief.label}</span>
        <span className="shrink-0 rounded-full bg-surface-elevated px-2 py-0.5 text-2xs font-medium text-muted">
          #{rank}
        </span>
      </div>

      <dl className="grid grid-cols-4 gap-2 text-center">
        <div className="flex flex-col">
          <dt className="text-2xs uppercase tracking-wider text-muted">IPs</dt>
          <dd className="text-sm font-semibold tabular-nums text-primary">
            {numberFormat.format(thief.distinctIps)}
          </dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-2xs uppercase tracking-wider text-muted">Countries</dt>
          <dd className="text-sm font-semibold tabular-nums text-primary">{thief.countries}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-2xs uppercase tracking-wider text-muted">Acc.</dt>
          <dd className="text-sm font-semibold tabular-nums text-primary">{thief.accounts}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-2xs uppercase tracking-wider text-muted">Chann.</dt>
          <dd className="text-sm font-semibold tabular-nums text-primary">{thief.channels}</dd>
        </div>
      </dl>

      <div
        className="flex flex-col gap-1.5 border-t border-border pt-3"
        style={{ opacity: 0.5 + unavailPct * 0.5 }}
      >
        <div className="flex items-center justify-between text-2xs text-muted">
          <span>CDN unavailability</span>
          <span className="font-medium tabular-nums text-status-warn-text">
            {percentFormat.format(unavailPct)} · {formatDurationHM(thief.unavailDurationMin)}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-status-warn/20">
          <div
            className="h-full rounded-full bg-status-warn"
            style={{ width: `${unavailPct * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export function Component({ data, config }: WidgetProps<EventCdnThievesConfig>) {
  const error = firstError(data, ["thieves"]);
  const result = unwrap<EventCdnThievesData>(data.thieves);
  const loading = data.thieves?.isLoading && !result;
  const heading = config.title ?? manifest.name;

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="cards" />
      ) : result.thieves.length === 0 ? (
        <WidgetState title="No CDN theft detected for this event." />
      ) : (
        <div className="p-4">
          <Carousel>
            {result.thieves.map((thief, index) => (
              <ThiefCard key={thief.id} thief={thief} rank={index + 1} />
            ))}
          </Carousel>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<EventCdnThievesConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
